import { Button, EmptyState, PageHeader, Spinner } from '@repo/ui'
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router'
import { ArrowLeft, ChevronRight, Repeat } from 'lucide-react'

import { useAuth } from '@/hooks/use-auth'
import { useCadence } from '@/hooks/use-cadences'

export const Route = createFileRoute('/_app/cadencias/$cadenceId')({
  component: CadenceDetailLayout,
})

const TABS = [
  { to: '/cadencias/$cadenceId', label: 'Visão geral', suffix: '' },
  { to: '/cadencias/$cadenceId/etapas', label: 'Etapas', suffix: '/etapas' },
  {
    to: '/cadencias/$cadenceId/agenda',
    label: 'Janela e regras',
    suffix: '/agenda',
  },
  { to: '/cadencias/$cadenceId/leads', label: 'Leads', suffix: '/leads' },
  {
    to: '/cadencias/$cadenceId/relatorio',
    label: 'Relatório',
    suffix: '/relatorio',
  },
] as const

function CadenceDetailLayout() {
  const { cadenceId } = Route.useParams()
  const location = useLocation()
  const { currentOrgId } = useAuth()
  const {
    data: cadence,
    isLoading,
    isError,
  } = useCadence(currentOrgId, cadenceId)

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="text-muted-foreground h-6 w-6" />
      </div>
    )
  }

  if (isError || !cadence) {
    return (
      <EmptyState
        variant="inline"
        className="min-h-80"
        icon={Repeat}
        title="Cadência não encontrada"
        description="Ela pode ter sido removida ou pertencer a outra organização."
        action={
          <Button asChild variant="outline">
            <Link to="/cadencias">
              <ArrowLeft className="h-4 w-4" />
              Voltar para Cadências
            </Link>
          </Button>
        }
      />
    )
  }

  const activeTab =
    TABS.find((tab) =>
      tab.suffix
        ? location.pathname.endsWith(tab.suffix)
        : location.pathname === `/cadencias/${cadenceId}`
    ) ?? TABS[0]

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <nav className="text-muted-foreground mb-3 flex flex-wrap items-center gap-1.5 text-sm">
          <Link to="/cadencias" className="hover:text-foreground">
            Cadências
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{cadence.name}</span>
          <ChevronRight className="h-3 w-3" />
          <span>{activeTab.label}</span>
        </nav>
        <PageHeader variant="plain" className="mb-4" title={cadence.name} />
        <div className="border-border mb-6 flex items-center gap-1 border-b">
          {TABS.map((tab) => {
            const active = activeTab.to === tab.to
            return (
              <Link
                key={tab.to}
                to={tab.to}
                params={{ cadenceId }}
                className={`relative px-4 py-2.5 text-[15px] transition-colors ${
                  active
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                {active ? (
                  <span className="bg-primary absolute inset-x-0 -bottom-px h-0.5" />
                ) : null}
              </Link>
            )
          })}
        </div>
        <Outlet />
      </div>
    </div>
  )
}
